// components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { ConfirmationDialog, Dialog } from 'components/shared';
import DateStepperField from 'components/shared/DateStepperField/DateStepperField';
import ConsequencesPanel from './ConsequencesPanel';
import WorkAgreementSection from './WorkAgreementSection';

// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterAbsenceDialog } from './useRegisterAbsenceDialog';

// styles
import {
  Column,
  DayCount,
  DialogColumns,
  ErrorBox,
  HintText,
  NoticeBanner,
  PeriodRow,
  SectionPanel,
  SectionTitle
} from './RegisterAbsenceDialog.styles';

// utils
import { formatByLocale, parseBackendDateTime } from 'utils/date';

// types
import type { RegisterAbsenceDialogProps } from './RegisterAbsenceDialog.types';

/**
 * React replacement of the Dojo "Afwezigheid vastleggen" dialog for the
 * absence-notice flows: sickness only (ZK/ZKZ), an explicit work agreement
 * choice, and a live panel showing the consequences for the planning.
 * Enabled through the FEATURE_TOGGLE_REACT_REGISTER_ABSENCE grant.
 */
const RegisterAbsenceDialog = ({
  open,
  employee,
  notice,
  absenceId,
  onClose,
  onSuccess
}: RegisterAbsenceDialogProps) => {
  const { t } = useTranslation('common');
  const [noteTouched, setNoteTouched] = useState(false);
  const [confirmNoConsequences, setConfirmNoConsequences] = useState(false);
  const form = useRegisterAbsenceDialog({
    open,
    employee,
    initialStartDate: notice ? new Date(notice.start) : undefined,
    initialEndDate: notice ? new Date(notice.end) : undefined,
    absenceId,
    onClose,
    onSuccess
  });

  const title = `${t(
    form.isEdit
      ? 'reactRegisterAbsence.editTitle'
      : 'reactRegisterAbsence.title'
  )} — ${[employee.name, employee.number ? `(${employee.number})` : null]
    .filter(Boolean)
    .join(' ')}`;

  return (
    <Dialog
      open={open}
      title={title}
      handleClose={onClose}
      maxWidth={false}
      // tells this dialog apart from the confirmation dialog it can open
      data-testid="register-absence-dialog"
      actions={
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Tooltip
            title={
              form.hasBlockingOverlap
                ? t('reactRegisterAbsence.overlapBlocksSave')
                : form.startBeforeOriginal
                ? t('reactRegisterAbsence.startBeforeOriginal')
                : form.endAfterOriginal
                ? t('reactRegisterAbsence.endAfterOriginal')
                : !form.isEdit && form.selectedWaIds.length === 0
                ? t('reactRegisterAbsence.workAgreementRequired')
                : !form.note.trim()
                ? t('reactRegisterAbsence.noteRequired')
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                disabled={!form.canSave}
                onClick={() => {
                  // saving without any effect on the planning is possible,
                  // but never silently; editing without changes is harmless
                  if (!form.isEdit && form.hasNoConsequences) {
                    setConfirmNoConsequences(true);
                    return;
                  }
                  form.save();
                }}
              >
                {t('save')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      }
    >
      <Box sx={{ p: 2 }}>
        {notice && (
          <NoticeBanner>
            {t(
              notice.reason
                ? 'reactRegisterAbsence.noticeBannerWithReason'
                : 'reactRegisterAbsence.noticeBanner',
              {
                name: notice.createdBy ?? employee.name ?? '',
                from: formatByLocale(
                  parseBackendDateTime(notice.start),
                  'd MMM'
                ),
                to: formatByLocale(parseBackendDateTime(notice.end), 'd MMM'),
                reason: notice.reason
              }
            )}
          </NoticeBanner>
        )}

        <DialogColumns>
          {/* left column: what and for whom. Scrolls inside the fixed dialog
              height, so unfolding the working-days box grows this column
              instead of the dialog */}
          <Column sx={{ overflowY: 'auto', pr: 0.5 }}>
            <SectionPanel>
              <SectionTitle>
                {t('reactRegisterAbsence.periodSection')}
              </SectionTitle>
              {/* the two dates stay side by side; only the day count moves to
                  the next line when a long period ("105 dagen") needs the
                  room, so the period never breaks in two */}
              <PeriodRow
                sx={{
                  flexWrap: 'wrap',
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: 'start'
                }}
              >
                <DateStepperField
                  testId="absence-period-from"
                  label={t('reactRegisterAbsence.from')}
                  date={form.startDate}
                  minDate={form.originalStartDate ?? undefined}
                  maxDate={form.originalEndDate ?? undefined}
                  onChange={form.setStartDate}
                  error={
                    form.startBeforeOriginal
                      ? {
                          type: 'validate',
                          message: t('reactRegisterAbsence.startBeforeOriginal')
                        }
                      : undefined
                  }
                  previousDayLabel={t('reactRegisterAbsence.previousDay')}
                  nextDayLabel={t('reactRegisterAbsence.nextDay')}
                />
                <DateStepperField
                  testId="absence-period-to"
                  label={t('reactRegisterAbsence.to')}
                  date={form.endDate}
                  minDate={form.originalStartDate ?? undefined}
                  maxDate={form.originalEndDate ?? undefined}
                  onChange={form.setEndDate}
                  error={
                    form.endBeforeStart
                      ? {
                          type: 'validate',
                          message: t('reactRegisterAbsence.endBeforeStart')
                        }
                      : form.endAfterOriginal
                      ? {
                          type: 'validate',
                          message: t('reactRegisterAbsence.endAfterOriginal')
                        }
                      : undefined
                  }
                  previousDayLabel={t('reactRegisterAbsence.previousDay')}
                  nextDayLabel={t('reactRegisterAbsence.nextDay')}
                />
                {/* the duration belongs visually to the end date, and is the
                    only part that may move to a line of its own */}
                <DayCount sx={{ alignSelf: 'center' }}>
                  {form.dayCount === 1
                    ? t('reactRegisterAbsence.oneDay')
                    : t('reactRegisterAbsence.dayCount', {
                        count: form.dayCount
                      })}
                </DayCount>
              </PeriodRow>
              {/* editing can only shrink the period; the original period is
                  the reference the planner shrinks within */}
              {form.isEdit &&
                form.originalStartDate &&
                form.originalEndDate && (
                  <HintText sx={{ mt: 1.5, mb: 0 }}>
                    {t('reactRegisterAbsence.originalPeriodHint', {
                      from: formatByLocale(
                        form.originalStartDate,
                        'd MMM yyyy'
                      ),
                      to: formatByLocale(form.originalEndDate, 'd MMM yyyy')
                    })}
                  </HintText>
                )}
              {/* saving is pointless here, so this is said at the source (the
                  period) instead of only through a disabled save button */}
              {form.overlappingAbsences.length > 0 && (
                <ErrorBox sx={{ mt: 2 }}>
                  <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('reactRegisterAbsence.overlapWarningTitle')}
                  </Box>
                  {form.overlappingAbsences.map((absence) => (
                    <Box key={absence.entityId}>
                      {t('reactRegisterAbsence.overlapWarningLine', {
                        from: formatByLocale(
                          parseBackendDateTime(absence.start),
                          'd MMM'
                        ),
                        to: formatByLocale(
                          parseBackendDateTime(absence.end),
                          'd MMM'
                        ),
                        workAgreement: absence.workAgreement
                      })}
                    </Box>
                  ))}
                </ErrorBox>
              )}
              <TextField
                select
                size="small"
                sx={{ mt: 2, minWidth: 240 }}
                label={t('reactRegisterAbsence.dutyHourType')}
                value={form.dutyTypeId}
                onChange={(event) =>
                  form.setDutyTypeId(Number(event.target.value))
                }
              >
                {form.dutyHourTypes.map((dutyHourType) => (
                  <MenuItem
                    key={dutyHourType.dutyTypeId}
                    value={dutyHourType.dutyTypeId}
                  >
                    {dutyHourType.description}
                    {dutyHourType.flags?.includes('REQ_DISCONNECT_AUTO') &&
                      ` ${t(
                        'reactRegisterAbsence.dutyHourTypeDetachesSuffix'
                      )}`}
                  </MenuItem>
                ))}
              </TextField>
            </SectionPanel>

            <SectionPanel>
              <SectionTitle>
                {t('reactRegisterAbsence.workAgreementSection')}
              </SectionTitle>
              {form.isEdit ? (
                // the backend cannot move an absence to another work
                // agreement or percentage, so editing shows them fixed
                <>
                  {form.workAgreements.map((workAgreement) => (
                    <Box key={workAgreement.waId} sx={{ fontSize: 14 }}>
                      {/* same line as when registering, contract kind
                          included: it explains the work agreement here too */}
                      {[
                        workAgreement.nr,
                        workAgreement.brName,
                        workAgreement.functionDescription,
                        workAgreement.contractKind,
                        `${form.percentage}%`
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Box>
                  ))}
                  <HintText sx={{ mt: 1, mb: 0 }}>
                    {t('reactRegisterAbsence.editWorkAgreementFixed')}
                  </HintText>
                </>
              ) : (
                <WorkAgreementSection
                  workAgreements={form.workAgreements}
                  selectedWaIds={form.selectedWaIds}
                  onChange={form.setSelectedWaIds}
                  dutyCountByWaId={form.dutyCountByWaId}
                  settings={form.workAgreementSettings}
                  onSettingChange={form.updateWorkAgreementSetting}
                  percentage={form.percentage}
                  onPercentageChange={form.setPercentage}
                  benefitChangeByWaId={form.benefitChangeByWaId}
                  isWorkingDaysChanged={form.isWorkingDaysChanged}
                  onSaveWorkingDays={(waId) =>
                    form.saveWorkingDaysSetting([waId])
                  }
                />
              )}
            </SectionPanel>
          </Column>

          {/* right column: the result, and the reason as the closing step */}
          <Column>
            {/* takes all remaining height, so as many duty lines as possible
                are visible without scrolling */}
            <SectionPanel
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              <SectionTitle>
                {t('reactRegisterAbsence.consequencesSection')}
                <Box component="span" sx={{ fontWeight: 400, ml: 1 }}>
                  ({t('reactRegisterAbsence.consequencesLive')})
                </Box>
              </SectionTitle>
              <ConsequencesPanel
                existingDutyLines={form.existingDutyLines}
                newDutyLines={form.selectedNewDutyLines}
                selectedWaIds={form.selectedWaIds}
                workAgreementLabelById={Object.fromEntries(
                  form.workAgreements.map((workAgreement) => [
                    workAgreement.waId,
                    [workAgreement.nr, workAgreement.brName]
                      .filter(Boolean)
                      .join(' · ')
                  ])
                )}
                hasSelection={form.isEdit || form.selectedWaIds.length > 0}
                isLoading={
                  form.isEdit ? form.isLoadingAbsence : form.isRolloutLoading
                }
                hasResult={form.hasConsequencesResult}
                dutyHourTypeDescription={
                  form.dutyHourTypes.find(
                    (dutyHourType) =>
                      dutyHourType.dutyTypeId === form.dutyTypeId
                  )?.description
                }
                detachedGivesOpenDuty={form.detachedGivesOpenDuty}
                rolloutErrorKey={form.rolloutErrorKey}
                isEdit={form.isEdit}
              />
            </SectionPanel>

            {/* closing step: the reason, pinned to the bottom */}
            <SectionPanel sx={{ mt: 'auto' }}>
              <SectionTitle>{t('reason')}</SectionTitle>
              <TextField
                fullWidth
                size="small"
                // "verplicht veld" instead of an asterisk, so the requirement
                // is readable rather than a symbol to decode
                label={t('reactRegisterAbsence.requiredField')}
                value={form.note}
                onChange={(event) => form.setNote(event.target.value)}
                error={noteTouched && !form.note.trim()}
                helperText={
                  noteTouched && !form.note.trim()
                    ? t('reactRegisterAbsence.noteRequired')
                    : undefined
                }
                onBlur={() => setNoteTouched(true)}
                multiline
                minRows={2}
              />
            </SectionPanel>
          </Column>
        </DialogColumns>
      </Box>

      {confirmNoConsequences && (
        <ConfirmationDialog
          type="warning"
          title={t('reactRegisterAbsence.noConsequencesTitle')}
          cancelBtnText={t('reactRegisterAbsence.goBack')}
          confirmBtnText={t('reactRegisterAbsence.processAnyway')}
          handleAction={(isConfirmed) => {
            setConfirmNoConsequences(false);
            if (isConfirmed) {
              form.save();
            }
          }}
        >
          {t('reactRegisterAbsence.noConsequencesBody')}
        </ConfirmationDialog>
      )}
    </Dialog>
  );
};

export default RegisterAbsenceDialog;
